import React, { useState } from "react";
import { Box, IconButton, Menu, MenuItem, Typography, useTheme } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { RootState } from "utils/store";
import { deleteMultipleReviewer, updateIndividualReviewer } from "redux/appSlice";
import { normalizeReviewerOrders } from "utils/signerOrderUtils";
import { isTruthy } from "helpers/methods";
import strings from "global/constants/StringConstants";
import { Reviewers } from "models/interfaces";
import MobileContactAutocomplete from "./MobileContactAutocomplete";
import { MobileSignRequestStyles } from "../MobileSignRequest.styles";

interface MobileApproverCardProps {
  reviewer: Reviewers;
  displayIndex: number;
  allReviewers: Reviewers[];
  excludeIds: string[];
  setExcludeIds: Function;
  contactList: any;
  setContactList: Function;
}

const MobileApproverCard: React.FC<MobileApproverCardProps> = ({
  reviewer,
  displayIndex,
  allReviewers,
  excludeIds,
  setExcludeIds,
  contactList,
  setContactList,
}) => {
  const theme = useTheme();
  const m = theme.palette.mobile;
  const classes = MobileSignRequestStyles(theme);
  const dispatch = useAppDispatch();
  const errors = useAppSelector((s: RootState) => s.app.errors);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  const update = (fields: Partial<Reviewers>) =>
    dispatch(
      updateIndividualReviewer({ reviewerId: reviewer.id, fieldsToUpdate: fields }),
    );

  const handleDelete = () => {
    const remaining = allReviewers.filter((r) => r.id !== reviewer.id);
    dispatch(deleteMultipleReviewer(normalizeReviewerOrders(remaining)));
    if (reviewer.email) {
      setExcludeIds(excludeIds.filter((id: string) => id !== reviewer.email));
    }
    setMenuAnchor(null);
  };

  return (
    <Box sx={classes.signerCard}>
      <Box sx={classes.signerCardHead}>
        <Box sx={classes.signerCardHeadLeft}>
          <Box
            aria-hidden="true"
            sx={classes.signerBadge(reviewer.color || m.ePurple[500])}
          >
            {displayIndex + 1}
          </Box>
          <Typography sx={classes.signerCardTitle}>
            Approvers {displayIndex + 1}
          </Typography>
        </Box>
        <IconButton
          size="small"
          aria-label={`Options for approver ${displayIndex + 1}`}
          aria-haspopup="menu"
          aria-expanded={Boolean(menuAnchor)}
          aria-controls={menuAnchor ? `approver-${reviewer.id}-menu` : undefined}
          sx={{ p: 0.5, color: m.base[500] }}
          onClick={(e) => setMenuAnchor(e.currentTarget)}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu
          id={`approver-${reviewer.id}-menu`}
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          MenuListProps={{ "aria-label": "Approver options" }}
        >
          <MenuItem onClick={handleDelete}>
            <Typography variant="subtitle2">Delete</Typography>
          </MenuItem>
        </Menu>
      </Box>

      <MobileContactAutocomplete
        field="name"
        id={`approver-${reviewer.id}-name`}
        label="Full Name"
        placeholder="Enter full name"
        value={reviewer.name || ""}
        index={reviewer.id}
        currentEmail={reviewer.email || ""}
        contactList={contactList}
        setContactList={setContactList}
        excludeIds={excludeIds}
        setExcludeIds={setExcludeIds}
        onUpdate={update}
        error={!isTruthy(reviewer.name) && errors.name}
        errorMessage={
          !isTruthy(reviewer.name) && errors.name ? "Name is required" : ""
        }
      />

      <MobileContactAutocomplete
        field="email"
        id={`approver-${reviewer.id}-email`}
        label="Email Address"
        placeholder="Enter email address"
        value={reviewer.email || ""}
        index={reviewer.id}
        currentEmail={reviewer.email || ""}
        contactList={contactList}
        setContactList={setContactList}
        excludeIds={excludeIds}
        setExcludeIds={setExcludeIds}
        onUpdate={update}
        error={
          (!isTruthy(reviewer.email) || !strings.regex.test(reviewer.email)) &&
          errors.email
        }
      />
    </Box>
  );
};

export default MobileApproverCard;
